import React, { useState } from "react";
import {
  PrivateOfficeUpdateRequest,
  PictureProperty,
} from "@/services/private-office-service/types";
import { Button } from "@/components/ui/button";
import { privateOfficeService } from "@/services/private-office-service";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/features/authentication";
import { Trash2, AlertTriangle } from "lucide-react";
import {
  getImageDeletionProperty,
  isImageDeletionRequest,
  IMAGE_PROPERTY_NAMES,
  extractImagesFromOfficeInfo,
} from "@/utils/image-utils";
import { extractApiErrorMessage } from "@/utils/extract-api-error-message";

interface PendingImageDeletionRequestProps {
  request: PrivateOfficeUpdateRequest;
  onApproved: (req: PrivateOfficeUpdateRequest) => void;
  onRejected: (req: PrivateOfficeUpdateRequest) => void;
  onImageSlotApplied?: (
    roomId: number,
    property: PictureProperty,
    url: string | null,
  ) => void;
}

export const PendingImageDeletionRequest: React.FC<
  PendingImageDeletionRequestProps
> = ({ request, onApproved, onRejected, onImageSlotApplied }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const { userInfo } = useAuth();

  if (!isImageDeletionRequest(request.fields_to_update)) {
    return null;
  }

  const deletionProperty = getImageDeletionProperty(request.fields_to_update);
  const propertyName = deletionProperty
    ? IMAGE_PROPERTY_NAMES[deletionProperty]
    : "Imagem";

  // Get the previous image URL that will be deleted
  const previousImages = request.previous_values
    ? extractImagesFromOfficeInfo(request.previous_values)
    : null;
  const imageToDelete =
    deletionProperty && previousImages
      ? previousImages[deletionProperty]
      : null;

  const handleApprove = async () => {
    if (!deletionProperty) {
      toast({
        title: "Erro",
        description: "Não foi possível identificar a imagem para deletar",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsApproving(true);
      const data = await privateOfficeService.approveUpdateRequest(
        request._id,
        {
          userEmail: userInfo.profile?.email || "",
        },
      );

      toast({
        title: "Sucesso",
        description: "Solicitação de deleção aprovada com sucesso",
      });
      // Frees the slot for the sibling requests of the same room.
      onImageSlotApplied?.(request.office_info.id, deletionProperty, null);
      onApproved(data);
    } catch (error) {
      console.error("Erro ao aprovar deleção:", error);
      toast({
        title: "Erro",
        description: extractApiErrorMessage(
          error,
          "Erro ao aprovar solicitação de deleção",
        ),
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: "Erro",
        description: "Digite um motivo para a reprovação",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsRejecting(true);
      const data = await privateOfficeService.rejectUpdateRequest(
        request._id,
        rejectionReason,
      );

      toast({
        title: "Sucesso",
        description: "Solicitação de deleção reprovada",
      });
      onRejected(data);
    } catch (error) {
      console.error("Erro ao reprovar deleção:", error);
      toast({
        title: "Erro",
        description: extractApiErrorMessage(
          error,
          "Erro ao reprovar solicitação de deleção",
        ),
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
      setShowRejectionForm(false);
      setRejectionReason("");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-red-200 p-6 space-y-6">
      <div className="border-b border-red-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Solicitação de Deleção de Imagem - {request.office_info.name}
            </h3>
            <p className="text-sm text-gray-600">
              Solicitado por {request.requested_by.username} em{" "}
              {new Date(request.created_at).toLocaleDateString("pt-BR")}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Alerta de deleção */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-800 font-medium">
                Solicitação de Deleção de {propertyName}
              </h4>
              <p className="text-red-700 text-sm mt-1">
                Esta solicitação removerá permanentemente a{" "}
                {propertyName.toLowerCase()} da sala. Esta ação não pode ser
                desfeita.
              </p>
            </div>
          </div>
        </div>

        {/* Imagem atual que será deletada */}
        {imageToDelete ? (
          <div className="text-center">
            <h4 className="font-medium text-gray-900 mb-4">
              Imagem que será deletada
            </h4>
            <div className="relative max-w-sm mx-auto">
              <div
                className="group aspect-video rounded-xl overflow-hidden shadow-lg ring-2 ring-red-200 cursor-pointer hover:ring-red-300 transition-all duration-200 relative"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(true);
                }}
              >
                <img
                  src={imageToDelete}
                  alt={`${propertyName} atual`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                />
                {/* Overlay de deleção */}
                <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Será deletada
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                <Trash2 className="w-3 h-3 text-white" />
              </div>
              <div className="text-center mt-3">
                <p className="text-sm text-gray-600 font-medium">
                  {propertyName} atual (clique para ampliar)
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Esta imagem será removida permanentemente
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-600">
              A imagem atual da {propertyName.toLowerCase()} não está
              disponível, mas será removida quando aprovada.
            </p>
          </div>
        )}
      </div>

      {!showRejectionForm ? (
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleApprove();
            }}
            disabled={isApproving}
            loading={isApproving}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Aprovar Deleção
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setShowRejectionForm(true);
            }}
            variant="outline"
            className="border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Reprovar
          </Button>
        </div>
      ) : (
        <div className="pt-4 border-t border-gray-200 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da reprovação:
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Digite o motivo da reprovação..."
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleReject();
              }}
              loading={isRejecting}
              disabled={isRejecting || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirmar Reprovação
            </Button>
            <Button
              disabled={isRejecting}
              onClick={(e) => {
                e.stopPropagation();
                setShowRejectionForm(false);
                setRejectionReason("");
              }}
              variant="outline"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Modal para visualizar imagem ampliada */}
      {showImageModal && imageToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={imageToDelete}
              alt={`${propertyName} - Será deletada`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};
